/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { gql, useQuery } from '@apollo/client'
import { faUpload } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import { useMemo, type FC } from 'react'
import { Button, Col, FloatingLabel, Form, Row, Stack } from 'react-bootstrap'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import type { Query } from '../../gql/graphql'
import { StoreRegionEnum } from '../../helpers'
import FileUploader from '../FileUploader'
import { isImageValid } from '../FileUploader/utils'

const GET_STORES_QUERY = gql`
  query {
    stores(isVirtual: false, limit: 500) {
      id
      name
      region
      store
      address
    }
  }
`

export const validationSchema = yup.object().shape({
  name: yup.string().required('O nome é obrigatório').default('').min(0).max(255),
  description: yup.string().required('A descrição é obrigatória').default('').min(0).max(65535),
  legalDetails: yup.string().required('A informações legais são obrigatórias').default('').min(0).max(65535),
  image: yup.mixed<File>().test("is-valid-format", "O arquivo deve ser uma imagem", value => {
    if (value === null || value === undefined) {
      return true
    }

    if ('url' in value && typeof value?.url === 'string') {
      return true
    }

    return value instanceof File && isImageValid(value?.type)
  }).nullable(),
  thumbnail: yup.mixed<File>().test("is-valid-format", "O arquivo deve ser uma imagem", value => {
    if (value === null || value === undefined) {
      return true
    }

    if ('url' in value && typeof value?.url === 'string') {
      return true
    }

    return value instanceof File && isImageValid(value?.type)
  }).nullable(),
  status: yup.string().oneOf(['active', 'inactive']).default('inactive'),
  meta: yup.object().shape({
    nameLabel: yup.string().default('').max(255),
    extraFieldLabel: yup.string().default('').max(255)
  }).transform(value => {
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value)
      } catch {
        value = null
      }
    }

    return value && typeof value === 'object' ? value : {}
  }),
  address: yup.string(),
  store: yup.object().shape({
    id: yup.string().nullable().default(null)
  }).nullable(),
  limitOfSubscriptions: yup.number().nullable().min(0).max(10000).default(10),
  observation: yup.string().nullable(),
  date: yup.date().nullable(),
  sendNotificationViaEmail: yup.boolean().nullable().default(false),
  startedAt: yup.date().nullable(),
  endAt: yup.date().nullable(),
  updatedAt: yup.date(),
  createdAt: yup.date()
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormWorkshopEventHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormWorkshopEventHandleSubmit,
  initialValues?: Partial<ValidationSchema>,
  isLoading?: boolean
}

const IMAGE_RESOLUTION = {
  width: 1240,
  height: 300
}

const THUMBNAIL_RESOLUTION = {
  width: 370,
  height: 283
}

/**
 * FormWorkshopEvent is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormWorkshopEvent from './src/views/PostEdit/FormWorkshopEvent.tsx'
 *
 * const MyComponent = () => (
 *   <FormWorkshopEvent />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormWorkshopEvent: FC<Props> = ({
  onSubmit = () => { },
  initialValues = {},
  isLoading = false,
}) => {
  const { data, loading, called } = useQuery<Query>(GET_STORES_QUERY, {
    fetchPolicy: 'no-cache'
  })
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: false,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  if (!called || loading) {
    return (
      <Form>
        <Row>
          <Col md={12}>
            <p>Carregando...</p>
          </Col>
        </Row>
      </Form>
    )
  }

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Row>
        <Form.Group as={Col} md={8} className="mb-3">
          <FloatingLabel label="Nome do evento" className="mb-3">
            <Form.Control
              type='text'
              name='name'
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </FloatingLabel>
          {formik.touched.name && formik.errors.name && (
            <Form.Text muted>
              {formik.errors.name}
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group as={Col} md={4} className="mb-3">
          <FloatingLabel label="Situação" className="mb-3">
            <Form.Select
              size="sm"
              value={formik.values.status}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
              name="status"
            >
              <option>Selecione uma opção</option>
              <option value='active'>🌎 Pública</option>
              <option value='inactive'>🔒 Privada</option>
            </Form.Select>
          </FloatingLabel>
          {formik.touched.status && formik.errors.status && (
            <Form.Text muted>
              {formik.errors.status}
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group as={Col} md={4} className="mb-3">
          <FloatingLabel label="Limite de inscritos" className="mb-3">
            <Form.Control
              type='number'
              name='limitOfSubscriptions'
              value={formik.values.limitOfSubscriptions?.toString() ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </FloatingLabel>
          {formik.touched.limitOfSubscriptions && formik.errors.limitOfSubscriptions && (
            <Form.Text muted>
              {formik.errors.limitOfSubscriptions}
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group as={Col} md={4} className="mb-3">
          <FloatingLabel label="Enviar notificação de inscrição por e-mail?" className="mb-3">
            <Form.Select
              size="sm"
              value={formik.values.sendNotificationViaEmail ? 'true' : 'false'}
              onChange={event => {
                formik.setFieldValue('sendNotificationViaEmail', event.target.value === 'true')
              }}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
              name="sendNotificationViaEmail"
            >
              <option>Selecione uma opção</option>
              <option value='true'>
                Enviar e-mails de confirmação para os inscritos
              </option>
              <option value='false'>
                Não enviar e-mails de confirmação para os inscritos
              </option>
            </Form.Select>
          </FloatingLabel>
          {formik.touched.sendNotificationViaEmail && formik.errors.sendNotificationViaEmail && (
            <Form.Text muted>
              {formik.errors.sendNotificationViaEmail}
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group as={Col} md={4} className='mb-3'>
          <FloatingLabel label="Loja (opcional)">
            <Form.Select
              value={formik.values?.store?.id ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || loading}
              name="store.id"
            >
              <option value='null'>Selecione uma opção</option>
              {data?.stores?.map(store => (
                <option value={store?.id}>
                  {StoreRegionEnum[store?.region ?? 'unknown']} - {store?.name}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={4} className='mb-3'>
          <FloatingLabel label="Publicar em" className="mb-3">
            <Form.Control
              size="sm"
              type='datetime-local'
              name='startedAt'
              value={moment(formik?.values?.startedAt?.toString()).format('YYYY-MM-DDTHH:mm')}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </FloatingLabel>
          {formik.touched.startedAt && formik.errors.startedAt && (
            <Form.Text muted>
              {formik.errors.startedAt}
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group as={Col} md={4} className='mb-3'>
          <FloatingLabel label="Despublicar em" className="mb-3">
            <Form.Control
              size="sm"
              type='datetime-local'
              name='endAt'
              value={moment(formik?.values?.endAt?.toString()).format('YYYY-MM-DDTHH:mm')}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </FloatingLabel>
          {formik.touched.endAt && formik.errors.endAt && (
            <Form.Text muted>
              {formik.errors.endAt}
            </Form.Text>
          )}
        </Form.Group>
        <OverlayTrigger
          overlay={(
            <Tooltip>
              Está é a data real que o evento ocorrerá, utilizada para
              organização interna e exibição para os participantes.
            </Tooltip>
          )}
        >
          <Form.Group as={Col} md={4} className='mb-3'>
            <FloatingLabel label="Data em que o evento ocorrerá" className="mb-3">
              <Form.Control
                size="sm"
                type='datetime-local'
                name='date'
                value={moment(formik?.values?.date?.toString()).format('YYYY-MM-DDTHH:mm')}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={formik.isSubmitting || isLoading}
              />
            </FloatingLabel>
            {formik.touched.endAt && formik.errors.endAt && (
              <Form.Text muted>
                {formik.errors.endAt}
              </Form.Text>
            )}
          </Form.Group>
        </OverlayTrigger>
        <Form.Group as={Col} md={12} className="mb-3">
          <FloatingLabel label="Endereço onde ocorrerá o evento de workshop" className="mb-3">
            <Form.Control
              type='text'
              name='address'
              value={formik.values.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </FloatingLabel>
          {formik.touched.address && formik.errors.address && (
            <Form.Text muted>
              {formik.errors.address}
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <FloatingLabel label="Descrição do evento nos cards de apresentação" className="mb-3">
            <Form.Control
              type='text'
              name='description'
              as="textarea"
              placeholder="Leave a comment here"
              style={{ height: '120px' }}
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </FloatingLabel>
          {formik.touched.description && formik.errors.description && (
            <Form.Text muted>
              {formik.errors.description}
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <FloatingLabel label="Descrição detalhada do evento" className="mb-3">
            <Form.Control
              type='text'
              name='observation'
              as="textarea"
              placeholder="Leave a comment here"
              style={{ height: '160px' }}
              value={formik.values.observation ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </FloatingLabel>
          {formik.touched.observation && formik.errors.observation && (
            <Form.Text muted>
              {formik.errors.observation}
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <FloatingLabel label="Informações legais do evento" className="mb-3">
            <Form.Control
              type='text'
              name='legalDetails'
              as="textarea"
              placeholder="Leave a comment here"
              style={{ height: '160px' }}
              value={formik.values.legalDetails ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </FloatingLabel>
          {formik.touched.legalDetails && formik.errors.legalDetails && (
            <Form.Text muted>
              {formik.errors.legalDetails}
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group as={Col} md={6} className="mb-3">
          <FloatingLabel label="Rótulo do campo Nome (opcional, ex: Nome do Responsável)" className="mb-3">
            <Form.Control
              type='text'
              name='meta.nameLabel'
              value={formik.values.meta?.nameLabel ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </FloatingLabel>
          {formik.touched.meta?.nameLabel && formik.errors.meta?.nameLabel && (
            <Form.Text muted>
              {formik.errors.meta.nameLabel}
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group as={Col} md={6} className="mb-3">
          <FloatingLabel label="Campo extra do formulário (opcional, deixe vazio para não exibir)" className="mb-3">
            <Form.Control
              type='text'
              name='meta.extraFieldLabel'
              value={formik.values.meta?.extraFieldLabel ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </FloatingLabel>
          {formik.touched.meta?.extraFieldLabel && formik.errors.meta?.extraFieldLabel && (
            <Form.Text muted>
              {formik.errors.meta.extraFieldLabel}
            </Form.Text>
          )}
        </Form.Group>
        <Col md={12}>
          <Row>
            <Form.Group as={Col} md={6} className="mb-3">
              <Form.Label>
                Banner de detalhes do evento
                {' '}
                <small>(obrigatório utilizar a resolução 1240x300px)</small>
              </Form.Label>
              {formik.values.image && 'url' in formik.values.image && typeof formik.values.image?.url === 'string' ? (
                <div className="mb-2">
                  <img
                    src={formik.values.image.url}
                    alt="Imagem do evento"
                    style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }}
                  />
                </div>
              ) : (
                <Form.Text muted>
                  Nenhuma imagem enviada.
                </Form.Text>
              )}
              <br />
              <FileUploader
                variant='outline-primary'
                accept="image/*"
                size='sm'
                resolutionWidth={IMAGE_RESOLUTION.width}
                resolutionHeight={IMAGE_RESOLUTION.height}
                resolutionType='absolute'
                disabled={formik.isSubmitting || isLoading}
                onReceiveFiles={([{ file }]) => {
                  formik.setFieldValue('image', file)
                }}
              >
                <FontAwesomeIcon icon={faUpload} />
                {' '}
                Enviar arquivo
              </FileUploader>
              <Form.Text muted>
                Envie uma imagem para o evento de workshop. O tamanho máximo do arquivo é de
                {' '}
                <strong>2MB</strong>.
                {' '}
                O formato deve ser uma imagem válida (PNG, JPG, JPEG, GIF).
              </Form.Text>
            </Form.Group>
            <Form.Group as={Col} md={6} className="mb-3">
              <Form.Label>
                Miniatura dos cards de evento
                {' '}
                <small>(obrigatório utilizar a resolução 370x283px)</small>
              </Form.Label>
              {formik.values.thumbnail && 'url' in formik.values.thumbnail && typeof formik.values.thumbnail?.url === 'string' ? (
                <div className="mb-2">
                  <img
                    src={formik.values.thumbnail.url}
                    alt="Imagem do evento"
                    style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }}
                  />
                </div>
              ) : (
                <Form.Text muted>
                  Nenhuma imagem enviada.
                </Form.Text>
              )}
              <br />
              <FileUploader
                variant='outline-primary'
                accept="image/*"
                size='sm'
                resolutionWidth={THUMBNAIL_RESOLUTION.width}
                resolutionHeight={THUMBNAIL_RESOLUTION.height}
                resolutionType='absolute'
                disabled={formik.isSubmitting || isLoading}
                onReceiveFiles={([{ file }]) => {
                  formik.setFieldValue('thumbnail', file)
                }}
              >
                <FontAwesomeIcon icon={faUpload} />
                {' '}
                Enviar arquivo
              </FileUploader>
              <Form.Text muted>
                Envie uma imagem para o evento de workshop. O tamanho máximo do arquivo é de
                {' '}
                <strong>2MB</strong>.
                {' '}
                O formato deve ser uma imagem válida (PNG, JPG, JPEG, GIF).
              </Form.Text>
            </Form.Group>
          </Row>
        </Col>
        <Col md={12}>
          <Stack direction='horizontal' gap={2}>
            <div className="p-2">
              <Button
                variant="link"
                style={{ marginRight: 10 }} onClick={() => navigate('/workshops')}
                disabled={formik.isSubmitting || isLoading}
              >
                Voltar
              </Button>
              <Button
                type="submit"
                disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}
              >
                Salvar
              </Button>
            </div>
            <div className="p-2 ms-auto">
              <small>
                <small>
                  Modificado em:
                  {' '}
                  {moment(formik?.values?.updatedAt ?? formik?.values?.createdAt).format('LLL')}
                </small>
              </small>
            </div>
          </Stack>
        </Col>
        {process.env.NODE_ENV === 'development' && (
          <pre>
            {JSON.stringify(formik.values, undefined, 2)}
          </pre>
        )}
      </Row>
    </Form>
  )
}

export default FormWorkshopEvent
